import { type FormEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/Button'
import { ErrorBox } from '../components/ui/Card'
import { inputClass } from '../components/ui/FormField'

interface InviteInfo {
  email: string
  first_name: string
  last_name: string
}

export default function AcceptPortalInvite() {
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') ?? ''
  const { loginWithToken } = useAuth()
  const navigate = useNavigate()

  const [info, setInfo] = useState<InviteInfo | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!token) {
      setLoadError('Invalid invitation link — no token found.')
      return
    }
    fetch(`/api/auth/portal-invite/${token}`)
      .then(async (res) => {
        if (!res.ok) {
          const err = (await res.json()) as { detail?: string }
          throw new Error(err.detail ?? 'Invalid or expired link.')
        }
        return res.json() as Promise<InviteInfo>
      })
      .then(setInfo)
      .catch((err: unknown) => {
        setLoadError(err instanceof Error ? err.message : 'Invalid or expired link.')
      })
  }, [token])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      const res = await fetch('/api/auth/accept-portal-invite', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          token,
          password,
          full_name: info ? `${info.first_name} ${info.last_name}` : undefined,
        }),
      })
      if (!res.ok) {
        const err = (await res.json()) as { detail?: string }
        throw new Error(err.detail ?? 'Failed to set up account.')
      }
      const data = (await res.json()) as { access_token: string }
      await loginWithToken(data.access_token)
      void navigate('/dashboard', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to set up account.')
    } finally {
      setSubmitting(false)
    }
  }

  const pageClass = 'min-h-screen bg-bg flex items-center justify-center p-8'
  const cardClass = 'bg-surface rounded-xl shadow-card p-10 w-full max-w-[440px]'

  if (loadError) {
    return (
      <div className={pageClass}>
        <div className={`${cardClass} text-center`}>
          <h2 className="m-0 mb-3 text-text-heading">Link expired</h2>
          <p className="text-danger-text mb-2">{loadError}</p>
          <p className="text-text-3 text-sm">Please ask your vet practice to send a new invitation.</p>
        </div>
      </div>
    )
  }

  if (!info) {
    return (
      <div className={pageClass}>
        <div className={`${cardClass} text-center`}>
          <p className="text-text-3">Verifying your invitation…</p>
        </div>
      </div>
    )
  }

  return (
    <div className={pageClass}>
      <div className={cardClass}>
        <div className="mb-6 text-center">
          <h1 className="m-0 mb-1 text-[1.5rem] font-bold text-text-heading">Set up your account</h1>
          <p className="m-0 text-text-3 text-sm">Welcome, {info.first_name}! Choose a password to access your pet portal.</p>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} noValidate>
          <div className="mb-4">
            <label className="block text-sm font-medium text-text-2 mb-1">Email</label>
            <input
              type="email"
              value={info.email}
              readOnly
              className={`${inputClass()} opacity-60 cursor-default`}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-text-2 mb-1" htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              minLength={8}
              className={inputClass()}
              autoFocus
            />
          </div>

          <div className="mb-5">
            <label className="block text-sm font-medium text-text-2 mb-1" htmlFor="confirm">
              Confirm Password
            </label>
            <input
              id="confirm"
              type="password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              placeholder="Repeat your password"
              required
              className={inputClass()}
            />
          </div>

          {error && <ErrorBox className="mb-4">{error}</ErrorBox>}

          <Button type="submit" disabled={submitting} className="w-full">
            {submitting ? 'Setting up…' : 'Create Account & Sign In'}
          </Button>
        </form>
      </div>
    </div>
  )
}
